import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, ChevronRight } from 'lucide-react';
import { getDiaSemanaFromDate } from '../../api/hooks/useHorarios';
import { DIAS_UTEIS } from './useProfessorGradeSemanal';
import { toMin } from './professorUtils';
import type { DiaSemana, HorarioAula } from '../../types/models';

const DIA_NOME: Record<DiaSemana, string> = {
  SEGUNDA: 'Segunda',
  TERCA: 'Terça',
  QUARTA: 'Quarta',
  QUINTA: 'Quinta',
  SEXTA: 'Sexta',
  SABADO: 'Sábado',
};

function toIsoLocal(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function labelDia(iso: string, hojeIso: string): string {
  const d = new Date(iso + 'T12:00:00');
  const hoje = new Date(hojeIso + 'T12:00:00');
  const diff = Math.round((d.getTime() - hoje.getTime()) / 86400000);
  if (diff === 0) return 'Hoje';
  if (diff === 1) return 'Amanhã';
  const dia = getDiaSemanaFromDate(iso);
  const abrev = dia ? DIA_NOME[dia].slice(0, 3) : '';
  return `${abrev} ${d.getDate()}/${d.getMonth() + 1}`;
}

export interface ProximaAulaItem {
  iso: string;
  diaLabel: string;
  aula: HorarioAula;
  isHoje: boolean;
}

export function buildProximasAulasLista(
  slotsByDia: Record<DiaSemana, HorarioAula[]>,
  hojeIso: string,
  agoraMin: number,
  limite = 8,
  diasBusca = 21,
): ProximaAulaItem[] {
  const hoje = new Date(hojeIso + 'T12:00:00');
  const itens: ProximaAulaItem[] = [];

  for (let offset = 0; offset < diasBusca; offset++) {
    const d = new Date(hoje);
    d.setDate(hoje.getDate() + offset);
    const iso = toIsoLocal(d);
    const diaSemana = getDiaSemanaFromDate(iso);
    if (!diaSemana) continue;

    const aulas = slotsByDia[diaSemana] ?? [];
    const isHoje = offset === 0;

    for (const aula of aulas) {
      if (isHoje && aula.horaFim && toMin(aula.horaFim) <= agoraMin) continue;
      itens.push({
        iso,
        diaLabel: labelDia(iso, hojeIso),
        aula,
        isHoje,
      });
    }
  }

  itens.sort((a, b) => {
    if (a.iso !== b.iso) return a.iso.localeCompare(b.iso);
    return (a.aula.horaInicio || '').localeCompare(b.aula.horaInicio || '');
  });

  return itens.slice(0, limite);
}

interface Props {
  slotsByDia: Record<DiaSemana, HorarioAula[]>;
  hojeIso: string;
  agoraMin: number;
  variant?: 'strip' | 'sidebar';
  statusMap?: Record<string, string>;
  isLoading?: boolean;
  totalAulasSemana?: number;
}

export function DesktopProximasAulas({
  slotsByDia,
  hojeIso,
  agoraMin,
  variant = 'strip',
  statusMap = {},
  isLoading,
  totalAulasSemana = 0,
}: Props) {
  const navigate = useNavigate();
  const isSidebar = variant === 'sidebar';
  const limite = isSidebar ? 5 : 8;

  const proximas = useMemo(
    () => buildProximasAulasLista(slotsByDia, hojeIso, agoraMin, limite),
    [slotsByDia, hojeIso, agoraMin, limite],
  );

  const diasComAula = DIAS_UTEIS.filter((d) => (slotsByDia[d]?.length ?? 0) > 0).length;

  if (isLoading) {
    return (
      <section className="dash-desk-proximas dash-desk-proximas--loading" aria-busy="true">
        <div className="dash-desk-proximas-skeleton" />
      </section>
    );
  }

  return (
    <section
      className={`dash-desk-proximas${isSidebar ? ' dash-desk-proximas--sidebar' : ''}`}
      aria-label="Próximas aulas"
    >
      <div className="dash-desk-proximas-head">
        <div className="dash-desk-proximas-title">
          <CalendarClock size={14} style={{ color: '#2563eb' }} />
          <h3>Próximas aulas</h3>
          {!isSidebar && (
            <span className="dash-desk-proximas-meta">
              {totalAulasSemana} aulas/semana (grade) · {diasComAula} dia{diasComAula !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {!isSidebar && (
          <button
            type="button"
            className="dash-desk-proximas-link"
            onClick={() => navigate('/professor/chamada')}
          >
            Chamada <ChevronRight size={12} />
          </button>
        )}
      </div>

      {proximas.length === 0 ? (
        <p className="dash-desk-proximas-empty">
          {isSidebar
            ? 'Sem aulas previstas na grade.'
            : 'Nenhuma aula prevista nos próximos dias na sua grade. Revise o horário em Minhas Turmas.'}
        </p>
      ) : (
        <div
          className={isSidebar ? 'dash-desk-proximas-list' : 'dash-desk-proximas-track'}
          role="list"
        >
          {proximas.map((item) => {
            const done = item.isHoje && statusMap[item.aula.id] === 'CONCLUIDA';
            const key = `${item.iso}-${item.aula.id}`;
            if (isSidebar) {
              return (
                <article
                  key={key}
                  role="listitem"
                  className={`dash-desk-proximas-row${item.isHoje ? ' dash-desk-proximas-row--hoje' : ''}`}
                >
                  <span className="dash-desk-proximas-row-dia">{item.diaLabel}</span>
                  <span className="dash-desk-proximas-row-time">
                    {item.aula.horaInicio}–{item.aula.horaFim}
                  </span>
                  <span className="dash-desk-proximas-row-materia">
                    {item.aula.materia?.nome || `${item.aula.numeroAula}ª Aula`}
                  </span>
                  <span className="dash-desk-proximas-row-turma">{item.aula.turma?.nome}</span>
                  {item.isHoje && (
                    <span className={`dash-desk-proximas-card-status${done ? ' ok' : ''}`}>
                      {done ? 'OK' : 'Pend.'}
                    </span>
                  )}
                </article>
              );
            }
            return (
              <article
                key={key}
                role="listitem"
                className={`dash-desk-proximas-card${item.isHoje ? ' dash-desk-proximas-card--hoje' : ''}`}
              >
                <div className="dash-desk-proximas-card-dia">{item.diaLabel}</div>
                <time className="dash-desk-proximas-card-time">
                  {item.aula.horaInicio}–{item.aula.horaFim}
                </time>
                <div className="dash-desk-proximas-card-body">
                  <span className="dash-desk-proximas-card-materia">
                    {item.aula.materia?.nome || `${item.aula.numeroAula}ª Aula`}
                  </span>
                  <span className="dash-desk-proximas-card-turma">{item.aula.turma?.nome}</span>
                </div>
                {item.isHoje && (
                  <span className={`dash-desk-proximas-card-status${done ? ' ok' : ''}`}>
                    {done ? 'OK' : 'Pend.'}
                  </span>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
